import type { CSSProperties } from 'react';

// On-brand line-art illustrations for the marketing site's green gradient panels — white strokes with
// varied opacity for depth. Richer than a lone icon, fully self-contained (no image assets, scales crisply).

const STROKE = 'rgba(255,255,255,0.92)';

/**
 * A cross-section: a young plant above the soil line, its root system + the living rhizosphere below —
 * root nodules, microbes and nutrients around the roots. Pairs with the "From the rhizosphere up" card.
 */
export function RhizosphereArt({ className, style }: { className?: string; style?: CSSProperties }) {
  return (
    <svg viewBox="0 0 200 250" className={className} style={style} fill="none" stroke={STROKE}
      strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {/* soil surface + faint strata */}
      <path d="M8 95 Q55 89 100 94 T192 94" opacity="0.8" strokeWidth="2.2" />
      <path d="M12 150 Q100 143 188 150" opacity="0.15" />
      <path d="M16 200 Q100 194 184 200" opacity="0.11" />

      {/* above ground: stem, two leaves, growth tip */}
      <path d="M100 95 C99 78 101 66 100 46" />
      <path d="M100 64 C85 63 72 55 65 42 C81 41 95 49 100 64 Z" opacity="0.95" />
      <path d="M100 64 C91 58 82 52 70 47" opacity="0.5" strokeWidth="1.1" />
      <path d="M100 55 C115 53 128 45 135 32 C119 32 105 40 100 55 Z" opacity="0.95" />
      <path d="M100 55 C109 49 118 43 130 38" opacity="0.5" strokeWidth="1.1" />
      <path d="M100 46 C97 40 98 34 100 29 C102 34 103 40 100 46 Z" opacity="0.9" />

      {/* below ground: taproot + laterals + fine hairs */}
      <path d="M100 95 C101 118 98 138 100 160 C101 178 99 190 100 208" strokeWidth="1.9" />
      <path d="M100 116 C84 122 70 131 56 149 M64 139 C58 143 54 151 52 159 M74 133 C72 139 70 145 70 151" opacity="0.85" strokeWidth="1.3" />
      <path d="M100 128 C118 134 132 145 146 163 M138 153 C142 159 144 165 145 173 M128 147 C130 153 132 159 133 165" opacity="0.85" strokeWidth="1.3" />
      <path d="M100 152 C88 160 78 170 70 186 M78 178 C74 184 72 190 71 196" opacity="0.75" strokeWidth="1.2" />
      <path d="M100 168 C112 176 122 186 128 200" opacity="0.75" strokeWidth="1.2" />
      <g opacity="0.4" strokeWidth="0.9">
        <path d="M56 149l-6 6M60 153l-7 3M146 163l6 6M142 159l7 3M70 186l-6 5M128 200l6 4" />
      </g>

      {/* root nodules (nitrogen-fixing) */}
      <g opacity="0.9">
        <ellipse cx="70" cy="132" rx="3.4" ry="4.4" transform="rotate(-30 70 132)" />
        <ellipse cx="132" cy="147" rx="3.4" ry="4.4" transform="rotate(28 132 147)" />
        <ellipse cx="88" cy="160" rx="3" ry="4" transform="rotate(-20 88 160)" />
      </g>

      {/* rhizosphere microbes / nutrients */}
      <g opacity="0.55">
        <circle cx="46" cy="140" r="2.2" /><circle cx="58" cy="171" r="1.6" /><circle cx="80" cy="150" r="1.4" />
        <circle cx="120" cy="132" r="1.6" /><circle cx="150" cy="151" r="2.2" /><circle cx="112" cy="177" r="1.8" />
        <circle cx="90" cy="197" r="1.5" /><circle cx="138" cy="189" r="1.5" /><circle cx="64" cy="120" r="1.4" />
      </g>
      <g opacity="0.3">
        <circle cx="40" cy="181" r="1.2" /><circle cx="160" cy="179" r="1.2" /><circle cx="104" cy="146" r="1" />
        <circle cx="126" cy="205" r="1.2" /><circle cx="76" cy="207" r="1.1" /><circle cx="150" cy="121" r="1" />
      </g>
    </svg>
  );
}

/**
 * A tended field receding to the horizon under a sun, young crops in the rows.
 * Pairs with the "Rooted in science, grown for farmers" card.
 */
export function FieldArt({ className, style }: { className?: string; style?: CSSProperties }) {
  return (
    <svg viewBox="0 0 240 200" className={className} style={style} fill="none" stroke={STROKE}
      strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {/* sun */}
      <circle cx="196" cy="44" r="17" opacity="0.95" />
      <g opacity="0.6" strokeWidth="1.3">
        <path d="M196 16v-8M196 80v8M224 44h8M168 44h-8M216 24l6-6M176 64l-6 6M216 64l6 6M176 24l-6-6" />
      </g>
      {/* rolling horizon */}
      <path d="M0 104 Q60 86 120 100 T240 96" opacity="0.7" />
      <path d="M0 118 Q80 104 150 116 T240 110" opacity="0.4" />
      {/* field furrows in perspective */}
      <g opacity="0.85">
        <path d="M120 108 L12 196M120 108 L70 196M120 108 L120 196M120 108 L170 196M120 108 L228 196" />
      </g>
      {/* cross rows */}
      <g opacity="0.28" strokeWidth="1.2">
        <path d="M96 132 Q120 126 144 132M74 158 Q120 150 166 158M50 186 Q120 176 190 186" />
      </g>
      {/* young crops (two-leaf sprouts) */}
      <g opacity="0.9" strokeWidth="1.4">
        <path d="M 120 132 v -11 M 120 125 q -9 -2 -11 -12 q 7 3 11 12 M 120 123 q 9 -2 11 -12 q -7 3 -11 12" />
        <path d="M 100 160 v -9 M 100 155 q -7 -2 -9 -10 q 6 3 9 10 M 100 153 q 7 -2 9 -10 q -6 3 -9 10" />
        <path d="M 142 160 v -9 M 142 155 q -7 -2 -9 -10 q 6 3 9 10 M 142 153 q 7 -2 9 -10 q -6 3 -9 10" />
      </g>
    </svg>
  );
}
